import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/layout/AppShell";
import Spinner from "../components/common/Spinner";
import { getPlan, savePlan } from "../api/plan";
import { formatMoney } from "../utils/money";

// Going full time: Tyler's number (salary plus the benefits a W2 pays for),
// what Birdie Bus has to make to cover it, where things stand, and the steps
// in between. Inputs save as you type; the math reruns on every edit.

const MARK = "#2F8A57"; // validated brand green (see ExpenseCharts)
const INK = "#2D2D2D";
const SE_TAX = 0.0765; // the employer half of Social Security and Medicare an owner pays too

const usd0 = (c) => `${c < 0 ? "-" : ""}$${Math.abs(Math.round((Number(c) || 0) / 100)).toLocaleString()}`;
const sum = (xs) => xs.reduce((t, x) => t + x, 0);
const monthName = (key, add = 0, opts = { month: "long", year: "numeric" }) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1 + add, 1).toLocaleDateString([], opts);
};

// The plan itself. Items with a `gate` are tracked from the numbers, not ticked.
const PHASES = [
  {
    key: "done",
    title: "Already in place",
    items: [
      { id: "llc", t: "LLC formed with an operating agreement" },
      { id: "bank", t: "Business bank account and card" },
      { id: "insurance", t: "Commercial insurance" },
      { id: "booking", t: "Website, quotes and online payments" },
      { id: "bus", t: "Bus built and the simulator installed" },
    ],
  },
  {
    key: "number",
    title: "1. Know your number",
    when: "Now",
    items: [
      { id: "spending", t: "Add up your monthly personal spending", d: "Average three months of personal bank and card statements. It sets your runway goal below." },
      {
        id: "health_plan",
        t: "Line up health insurance",
        d: "Get quotes for your household on HealthCare.gov (Florida uses the federal marketplace), and from a spouse's employer if that's an option. Put the monthly cost in “Your number”.",
      },
      { id: "retirement", t: "Pick a retirement plan to replace the 401(k) match", d: "A SEP-IRA or Solo 401(k) are the usual options. A CPA can say which fits a partnership." },
      {
        id: "gabe_agreement",
        t: "Agree with Gabe how you'll be paid",
        d: "Gabe owns equity, so profit is split by ownership. Agree a fixed monthly payment for working full time (a guaranteed payment, paid before the split), and put it in the operating agreement.",
      },
      { id: "cpa", t: "Sit down with a CPA", d: "Quarterly estimated taxes, whether an S-corp election saves money once profit is steady, and what you can deduct." },
    ],
  },
  {
    key: "prove",
    title: "2. Prove it while you still have the paycheck",
    when: "The next 6 to 12 months",
    items: [
      { id: "gate_profit", gate: "profit", t: "Hit your event target three months in a row" },
      { id: "gate_booked", gate: "booked", t: "Be booked well ahead: half of the next three months' target" },
      { id: "reviews", t: "25+ Google reviews at 4.8★ or better", d: "Reviews are what make strangers book. Ask every happy host the day after." },
      {
        id: "corporate",
        t: "Land three repeat corporate clients",
        d: "Weekends alone top out around 8 to 12 events a month. Weekday corporate events and repeat clients are what make full time work.",
      },
      { id: "real_costs", t: "Log real costs on each job", d: "Miles, staff and extras on each job, so profit per event here is real, not estimated." },
      {
        id: "price_test",
        t: "Test a price increase",
        d: "Quote 10% higher for a month and watch your booking rate on Numbers. Every $100 more a booking lowers the events you need.",
      },
    ],
  },
  {
    key: "safety",
    title: "3. Build the safety net",
    when: "Alongside step 2",
    items: [
      { id: "gate_runway", gate: "runway", t: "Personal runway saved" },
      { id: "gate_reserve", gate: "reserve", t: "Business cash reserve in the bank" },
      { id: "card_in_full", t: "Pay the company card in full every month" },
      { id: "repair_fund", t: "Set aside a repair fund for the bus and generator", d: "A monthly amount in its own account, so one breakdown doesn't wreck a month." },
      { id: "breakdown_plan", t: "Have a breakdown plan", d: "A mechanic who works on the bus, a backup generator or rental, and a reschedule policy customers have agreed to." },
    ],
  },
  {
    key: "setup",
    title: "4. Set the business up to pay you",
    when: "2 to 3 months before",
    items: [
      {
        id: "tax_account",
        t: "Open a tax savings account",
        d: "Move a set share of every draw into it for quarterly estimated taxes (Apr 15, Jun 15, Sep 15, Jan 15). Your CPA will give you the percentage.",
      },
      { id: "draw_schedule", t: "Set a fixed monthly draw", d: "Same amount, same day each month, like a paycheck. Profit above it builds the reserve." },
      { id: "books_monthly", t: "Do the books monthly", d: "Upload statements on Expenses, clear the Review tab, check this page. About 30 minutes." },
      { id: "staff", t: "Hire and train one or two event staff", d: "So you can run two events in a day, or take a day off without turning work away." },
      { id: "insurance_review", t: "Review insurance for full-time volume", d: "Liability limits, commercial auto and equipment coverage for more events and more miles." },
    ],
  },
  {
    key: "jump",
    title: "5. Make the jump",
    when: "When every readiness check is green",
    items: [
      { id: "gate_all", gate: "all", t: "All six readiness checks green" },
      {
        id: "timing",
        t: "Pick a date going into the busy season",
        d: "In Southwest Florida that's roughly November to April, so your first months on your own are strong ones.",
      },
      { id: "coverage_gap", t: "No gap in health coverage", d: "Your new plan starts the day after your employer's ends." },
      { id: "rollover", t: "Roll over your 401(k)", d: "Into an IRA or your new plan, not cashed out." },
      { id: "notice", t: "Give notice on good terms", d: "Ask about part-time or contract work as a bridge for the first few months." },
    ],
  },
  {
    key: "first90",
    title: "6. Your first 90 days full time",
    items: [
      { id: "weekday_push", t: "Weekday sales push", d: "Company HR and event planners, schools, senior communities, golf clubs, breweries." },
      { id: "recurring", t: "Add recurring revenue", d: "Leagues, memberships or monthly corporate visits." },
      { id: "monthly_review", t: "Monthly check-in with Gabe", d: "Profit against this plan, your pay, and what's left to split by ownership." },
    ],
  },
];

function compute(s, data) {
  const salary = s.salary_cents;
  const seTax = s.self_employment_tax ? Math.round(salary * SE_TAX) : 0;
  const health = s.health_monthly_cents * 12;
  const retire = Math.round((salary * s.retirement_match_pct) / 100);
  const payYear = salary + seTax + health + retire + s.other_benefits_cents;
  const payMonth = payYear / 12;

  const lines = data.overhead.lines.map((l) => {
    const own = s.overhead[l.key];
    return { ...l, cents: own ?? l.suggested_cents ?? 0, own: own != null };
  });
  const overhead = sum(lines.map((l) => l.cents));
  const buffer = ((payMonth + overhead) * s.buffer_pct) / 100;
  const need = payMonth + overhead + buffer; // event profit needed each month

  const pe = data.per_event;
  const price = s.avg_price_cents ?? pe.revenue_cents;
  const costAt = (p) => pe.cost_cents + ((p - pe.revenue_cents) * data.card_fee_pct) / 100;
  const profitAt = (p) => p - costAt(p);
  const perEvent = profitAt(price);
  const eventsNeeded = perEvent > 0 ? Math.ceil(need / perEvent) : null;

  // The last three full months (this month is still going).
  const done = data.months.slice(0, -1);
  const last3 = done.slice(-3);
  const avgEvents = sum(last3.map((m) => m.events)) / 3;
  const avgProfit = sum(last3.map((m) => m.profit_cents)) / 3;
  const forYou = avgProfit - overhead;
  const pctThere = payMonth > 0 ? Math.max(0, Math.min(1, forYou / payMonth)) : 0;
  let streak = 0;
  for (let i = done.length - 1; i >= 0 && done[i].profit_cents >= need; i--) streak++;

  const bookedGoal = eventsNeeded ? 1.5 * eventsNeeded * price : 0; // half of three months
  const runwayGoal = s.personal_monthly_cents * s.runway_months;
  const reserveGoal = overhead * s.reserve_months;
  const gates = {
    profit: { ok: streak >= 3, label: "Profit covers it, three months running", detail: `${Math.min(streak, 3)} of 3 months so far` },
    booked: {
      ok: bookedGoal > 0 && data.upcoming.revenue_cents >= bookedGoal,
      label: "Booked well ahead",
      detail: `${usd0(data.upcoming.revenue_cents)} booked for the next 90 days, of ${usd0(bookedGoal)}`,
    },
    runway: {
      ok: runwayGoal > 0 && s.runway_saved_cents >= runwayGoal,
      label: "Personal runway",
      detail: runwayGoal > 0 ? `${usd0(s.runway_saved_cents)} of ${usd0(runwayGoal)} (${s.runway_months} months)` : "Add your monthly spending below",
    },
    reserve: {
      ok: reserveGoal > 0 && s.business_cash_cents >= reserveGoal,
      label: "Business cash reserve",
      detail: `${usd0(s.business_cash_cents)} of ${usd0(reserveGoal)} (${s.reserve_months} months of fixed costs)`,
    },
    health: { ok: !!s.checks.health_plan, label: "Health insurance lined up", detail: s.checks.health_plan ? "Done" : "Tick it in the plan below" },
    gabe: { ok: !!s.checks.gabe_agreement, label: "Pay agreed with Gabe", detail: s.checks.gabe_agreement ? "Done" : "Tick it in the plan below" },
  };
  const ready = Object.values(gates).filter((g) => g.ok).length;
  gates.all = { ok: ready === 6 };

  return {
    salary, seTax, health, retire, payYear, payMonth, lines, overhead, buffer, need, price, perEvent,
    profitAt, eventsNeeded, avgEvents, avgProfit, forYou, pctThere, streak, gates, ready, last3,
  };
}

export default function AdminPlan() {
  const [data, setData] = useState(null);
  const [s, setS] = useState(null);
  const [error, setError] = useState("");
  const [saveState, setSaveState] = useState("");
  const pending = useRef({});
  const timer = useRef(null);

  useEffect(() => {
    getPlan()
      .then((r) => {
        setData(r);
        setS(r.settings);
      })
      .catch((e) => setError(e.message || "Couldn't load the plan."));
  }, []);

  const flush = useCallback(async () => {
    const change = pending.current;
    pending.current = {};
    if (!Object.keys(change).length) return;
    setSaveState("Saving…");
    try {
      await savePlan(change);
      setSaveState("Saved");
    } catch (e) {
      setSaveState("");
      setError(e.message || "Couldn't save that.");
    }
  }, []);

  // Typing saves after a pause; leaving the page saves straight away.
  useEffect(() => () => {
    clearTimeout(timer.current);
    flush();
  }, [flush]);

  const update = (patch, now = false) => {
    setS((prev) => {
      const next = { ...prev, ...patch };
      if (patch.overhead) next.overhead = patch.overhead;
      if (patch.checks) next.checks = { ...prev.checks, ...patch.checks };
      return next;
    });
    const p = pending.current;
    pending.current = { ...p, ...patch, ...(patch.checks ? { checks: { ...(p.checks || {}), ...patch.checks } } : {}) };
    setError("");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, now ? 0 : 700);
  };

  const m = useMemo(() => (s && data ? compute(s, data) : null), [s, data]);

  return (
    <AppShell
      title="Full-time plan"
      wide
      action={saveState && <span className="text-xs text-birdie-charcoal/50">{saveState}</span>}
    >
      {error && <div className="mb-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {!m ? (
        !error && (
          <div className="py-16 flex justify-center">
            <Spinner label="Loading..." />
          </div>
        )
      ) : (
        <div className="space-y-4">
          <Hero m={m} />
          <div className="grid lg:grid-cols-2 gap-4 items-start">
            <Readiness m={m} />
            <Progress m={m} data={data} s={s} update={update} />
            <YourNumber m={m} s={s} update={update} />
            <BusinessNeeds m={m} s={s} data={data} update={update} />
            <WhatIf m={m} s={s} />
            <SafetyNet m={m} s={s} update={update} />
          </div>
          <PlanList s={s} m={m} update={update} />
          <p className="text-[11px] text-center text-birdie-charcoal/45 px-4">
            Estimates to plan with, not tax advice. Income tax is left out because it's about the same either way. A CPA can sharpen
            the numbers.
          </p>
        </div>
      )}
    </AppShell>
  );
}

function Card({ title, sub, children, className = "" }) {
  return (
    <section className={`bg-white border border-birdie-green/15 rounded-2xl p-4 min-w-0 ${className}`}>
      <div className="font-accent text-base text-birdie-charcoal">{title}</div>
      {sub && <p className="text-[11px] text-birdie-charcoal/55 mt-0.5">{sub}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Hero({ m }) {
  return (
    <div className="rounded-2xl bg-birdie-green text-white px-4 py-4 sm:px-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs text-white/70">Go full time at</div>
          <div className="text-[2.25rem] leading-tight font-semibold">
            {m.eventsNeeded ? `${m.eventsNeeded} events a month` : "Set your price"}
          </div>
          <p className="text-xs text-white/75 mt-1 max-w-xl">
            {m.eventsNeeded
              ? `At ${usd0(m.price)} a booking and about ${usd0(m.perEvent)} profit each, that pays you ${usd0(m.payMonth)} a month (your ${usd0(
                  m.salary
                )} salary plus benefits) and covers ${usd0(m.overhead)} of fixed costs.`
              : "Each event needs to earn more than it costs. Check the price and cost settings below."}
          </p>
        </div>
        <div className="sm:text-right shrink-0">
          <div className="text-xs text-white/70">Ready to go</div>
          <div className="text-2xl font-semibold">{m.ready} of 6</div>
        </div>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-[11px] text-white/75 mb-1">
          <span>
            Now: {+m.avgEvents.toFixed(1)} events a month, {usd0(Math.max(0, m.forYou))} a month for you
          </span>
          <span>{Math.round(m.pctThere * 100)}% there</span>
        </div>
        <div className="h-2.5 rounded-full bg-white/20" role="meter" aria-valuenow={Math.round(m.pctThere * 100)} aria-valuemin={0} aria-valuemax={100} aria-label="Progress to your full-time number">
          <div className="h-full rounded-full bg-white" style={{ width: `${Math.max(1.5, m.pctThere * 100)}%` }} />
        </div>
        <div className="text-[10px] text-white/55 mt-1">Average of the last three full months, after fixed costs.</div>
      </div>
    </div>
  );
}

function Readiness({ m }) {
  const order = ["profit", "booked", "runway", "reserve", "health", "gabe"];
  return (
    <Card title="Ready to go?" sub="Quit when all six are green. Until then, the paycheck is the safety net.">
      <ul className="space-y-2">
        {order.map((k) => {
          const g = m.gates[k];
          return (
            <li key={k} className="flex items-start gap-2.5">
              <span
                className={`mt-0.5 h-5 w-5 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold ${
                  g.ok ? "bg-birdie-green text-white" : "border-2 border-birdie-charcoal/20"
                }`}
                aria-hidden
              >
                {g.ok ? "✓" : ""}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-birdie-charcoal">
                  {g.label}
                  <span className={`ml-2 text-[10px] font-semibold whitespace-nowrap ${g.ok ? "text-birdie-green" : "text-birdie-charcoal/45"}`}>
                    {g.ok ? "Ready" : "Not yet"}
                  </span>
                </span>
                <span className="block text-[11px] text-birdie-charcoal/55">{g.detail}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ---- inputs -------------------------------------------------------------

function NumberInput({ value, onChange, prefix, suffix, step = 1, label, placeholder, width = "w-28" }) {
  const [text, setText] = useState(value == null ? "" : String(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(value == null ? "" : String(value));
  }, [value]);
  return (
    <span className={`inline-flex items-center rounded-lg border border-birdie-green/20 bg-white px-2 focus-within:ring-2 focus-within:ring-birdie-mustard/40 ${width}`}>
      {prefix && <span className="text-sm text-birdie-charcoal/50">{prefix}</span>}
      <input
        value={text}
        inputMode="decimal"
        step={step}
        aria-label={label}
        placeholder={placeholder}
        onFocus={() => (focused.current = true)}
        onBlur={() => {
          focused.current = false;
          setText(value == null ? "" : String(value));
        }}
        onChange={(e) => {
          const t = e.target.value.replace(/[^0-9.]/g, "");
          setText(t);
          onChange(t === "" ? null : Number(t));
        }}
        className="min-w-0 flex-1 bg-transparent py-1.5 px-1 text-sm text-right tabular-nums text-birdie-charcoal outline-none"
      />
      {suffix && <span className="text-xs text-birdie-charcoal/50">{suffix}</span>}
    </span>
  );
}

const dollars = (cents) => (cents == null ? null : Math.round(cents / 100));
const toCents = (d) => (d == null ? null : Math.round(d * 100));

function Row({ label, hint, children }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="min-w-0">
        <span className="block text-sm text-birdie-charcoal">{label}</span>
        {hint && <span className="block text-[11px] text-birdie-charcoal/50">{hint}</span>}
      </span>
      <span className="shrink-0">{children}</span>
    </div>
  );
}

function Line({ label, value, strong }) {
  return (
    <div className={`flex justify-between gap-3 py-1 text-sm ${strong ? "font-semibold text-birdie-charcoal border-t border-birdie-green/15 mt-1 pt-2" : "text-birdie-charcoal/75"}`}>
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

function YourNumber({ m, s, update }) {
  return (
    <Card title="Your number" sub="What it takes to match your W2 job: the salary plus what your employer pays for today.">
      <Row label="W2 salary" hint="per year">
        <NumberInput prefix="$" value={dollars(s.salary_cents)} onChange={(v) => update({ salary_cents: toCents(v ?? 0) })} label="W2 salary" />
      </Row>
      <Row label="Health insurance you'd pay" hint="per month, for your household">
        <NumberInput prefix="$" value={dollars(s.health_monthly_cents)} onChange={(v) => update({ health_monthly_cents: toCents(v ?? 0) })} label="Health insurance per month" />
      </Row>
      <Row label="401(k) match you'd lose" hint="percent of salary">
        <NumberInput suffix="%" value={s.retirement_match_pct} onChange={(v) => update({ retirement_match_pct: Math.min(25, v ?? 0) })} label="401(k) match" width="w-20" />
      </Row>
      <Row label="Other benefits" hint="per year: bonus, HSA, phone, anything else">
        <NumberInput prefix="$" value={dollars(s.other_benefits_cents)} onChange={(v) => update({ other_benefits_cents: toCents(v ?? 0) })} label="Other benefits" />
      </Row>
      <label className="flex items-start gap-2 py-2 text-sm text-birdie-charcoal cursor-pointer">
        <input
          type="checkbox"
          checked={s.self_employment_tax}
          onChange={(e) => update({ self_employment_tax: e.target.checked }, true)}
          className="mt-1 h-4 w-4 min-h-0 min-w-0 accent-birdie-green"
        />
        <span>
          Include self-employment tax
          <span className="block text-[11px] text-birdie-charcoal/50">
            Your employer pays half your Social Security and Medicare (7.65%) now. As an owner you pay both halves.
          </span>
        </span>
      </label>
      <div className="mt-2 rounded-xl bg-birdie-cream/60 px-3 py-2">
        <Line label="Salary" value={formatMoney(m.salary)} />
        {m.seTax > 0 && <Line label="Employer half of payroll taxes" value={formatMoney(m.seTax)} />}
        <Line label="Health insurance" value={formatMoney(m.health)} />
        <Line label="401(k) match" value={formatMoney(m.retire)} />
        {s.other_benefits_cents > 0 && <Line label="Other benefits" value={formatMoney(s.other_benefits_cents)} />}
        <Line label="Your number, per year" value={formatMoney(m.payYear)} strong />
        <Line label="Per month" value={formatMoney(m.payMonth)} />
      </div>
      {s.health_monthly_cents === 0 && (
        <p className="mt-2 text-[11px] text-amber-800">Health insurance is $0 for now. It's often the biggest surprise, so get a quote.</p>
      )}
    </Card>
  );
}

function BusinessNeeds({ m, s, data, update }) {
  const pe = data.per_event;
  const setLine = (key, cents) => {
    const next = { ...s.overhead };
    if (cents == null) delete next[key];
    else next[key] = cents;
    update({ overhead: next });
  };
  const basis = `${monthName(data.overhead.from.slice(0, 7), 0, { month: "short" })}–${monthName(data.overhead.to.slice(0, 7), 0, {
    month: "short",
    year: "numeric",
  })}`;
  return (
    <Card title="What Birdie Bus needs to make" sub="Your pay, plus the costs that come every month whether or not there are events.">
      <div className="text-xs font-semibold text-birdie-charcoal/60">Fixed costs, per month</div>
      {m.lines.map((l) => (
        <Row
          key={l.key}
          label={l.label}
          hint={
            l.own
              ? l.suggested_cents != null && (
                  <button type="button" onClick={() => setLine(l.key, null)} className="min-h-0 text-birdie-green font-semibold">
                    Use {usd0(l.suggested_cents)} from expenses
                  </button>
                )
              : l.suggested_cents != null
                ? `From your ${basis} expenses${l.key === "insurance" ? ". If a payment covered 6 or 12 months, enter the monthly amount" : ""}`
                : null
          }
        >
          <NumberInput prefix="$" value={dollars(l.cents)} onChange={(v) => setLine(l.key, toCents(v ?? 0))} label={l.label} width="w-24" />
        </Row>
      ))}
      <Line label="Fixed costs" value={`${formatMoney(m.overhead)} / month`} strong />

      <div className="mt-3">
        <Row label="Safety margin" hint="on top, for slow months">
          <NumberInput suffix="%" value={s.buffer_pct} onChange={(v) => update({ buffer_pct: Math.min(50, v ?? 0) })} label="Safety margin" width="w-20" />
        </Row>
      </div>

      <div className="mt-3 rounded-xl bg-birdie-cream/60 px-3 py-2">
        <Line label="Your pay" value={formatMoney(m.payMonth)} />
        <Line label="Fixed costs" value={formatMoney(m.overhead)} />
        <Line label={`Safety margin (${s.buffer_pct}%)`} value={formatMoney(m.buffer)} />
        <Line label="Profit needed from events, per month" value={formatMoney(m.need)} strong />
      </div>

      <div className="mt-3">
        <div className="text-xs font-semibold text-birdie-charcoal/60">Per event</div>
        <Row
          label="Average booking"
          hint={
            s.avg_price_cents != null ? (
              <button type="button" onClick={() => update({ avg_price_cents: null }, true)} className="min-h-0 text-birdie-green font-semibold">
                Use your real average ({usd0(pe.revenue_cents)})
              </button>
            ) : pe.basis === "jobs" ? (
              `Your average over ${pe.jobs} booked job${pe.jobs === 1 ? "" : "s"}, before sales tax`
            ) : pe.basis === "package" ? (
              `Your ${pe.package} price, until there are booked jobs`
            ) : (
              "No jobs or packages yet"
            )
          }
        >
          <NumberInput
            prefix="$"
            value={dollars(m.price)}
            onChange={(v) => update({ avg_price_cents: v == null ? null : toCents(v) })}
            label="Average booking"
            width="w-24"
          />
        </Row>
        <Line label="Costs per event (staff, fuel, card fees)" value={formatMoney(m.price - m.perEvent)} />
        <Line label="Profit per event" value={formatMoney(m.perEvent)} strong />
        {!data.cost_model_configured && (
          <p className="mt-1 text-[11px] text-amber-800">
            Staff and fuel aren't counted yet. Set them under Profit per event on{" "}
            <Link to="/admin/numbers" className="underline">
              Numbers
            </Link>{" "}
            so this is real.
          </p>
        )}
      </div>

      <div className="mt-3 rounded-xl border border-birdie-green/20 px-3 py-2.5">
        <div className="text-sm text-birdie-charcoal">
          {m.eventsNeeded ? (
            <>
              <span className="font-semibold">{m.eventsNeeded} events a month</span>, about{" "}
              <span className="font-semibold">{usd0(m.eventsNeeded * m.price)}</span> in bookings.
            </>
          ) : (
            "Each event has to earn more than it costs before there's a number."
          )}
        </div>
        <Row label="Most events you can do in a month" hint="With one bus and the crew you have">
          <NumberInput value={s.max_events_month} onChange={(v) => update({ max_events_month: Math.max(1, Math.min(100, Math.round(v ?? 1))) })} label="Most events a month" width="w-20" />
        </Row>
        {m.eventsNeeded > s.max_events_month && (
          <p className="text-[11px] text-amber-800">
            That's more than you can do. Close the gap with higher prices, longer bookings, weekday corporate events, or a second crew
            for two events a day.
          </p>
        )}
      </div>
    </Card>
  );
}

function Progress({ m, data, s, update }) {
  const [asTable, setAsTable] = useState(false);
  const cur = data.months[data.months.length - 1].month;
  let projection = null;
  if (m.eventsNeeded) {
    if (m.avgEvents >= m.eventsNeeded) projection = "You're averaging enough events already. Keep it up three months running.";
    else if (s.growth_events_month > 0) {
      const k = Math.ceil((m.eventsNeeded - m.avgEvents) / s.growth_events_month);
      projection = `At that pace you'd reach ${m.eventsNeeded} a month around ${monthName(cur, k)}. Three strong months later, ${monthName(
        cur,
        k + 3
      )} is the earliest sensible jump.`;
    }
  }
  return (
    <Card title="Where you are" sub="Events each month against what you need.">
      <div className="flex justify-end -mt-1 mb-1">
        <button type="button" onClick={() => setAsTable((v) => !v)} className="min-h-0 text-[11px] font-semibold text-birdie-charcoal/50 hover:text-birdie-green">
          {asTable ? "Show chart" : "Show as table"}
        </button>
      </div>
      {asTable ? (
        <table className="w-full text-sm">
          <caption className="sr-only">Events and profit by month</caption>
          <thead>
            <tr className="text-[11px] text-birdie-charcoal/50 text-left">
              <th className="font-medium py-1">Month</th>
              <th className="font-medium py-1 text-right">Events</th>
              <th className="font-medium py-1 text-right">Profit</th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((x) => (
              <tr key={x.month} className="border-t border-birdie-green/10">
                <td className="py-1.5 text-birdie-charcoal/70">{monthName(x.month)}</td>
                <td className="py-1.5 text-right tabular-nums">{x.events}</td>
                <td className="py-1.5 text-right tabular-nums">{formatMoney(x.profit_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <EventsChart months={data.months} target={m.eventsNeeded} />
      )}
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Stat label="Last 3 months" value={`${+m.avgEvents.toFixed(1)} events / mo`} sub={`${usd0(m.avgProfit)} profit / mo`} />
        <Stat label="Booked, next 90 days" value={`${data.upcoming.events} event${data.upcoming.events === 1 ? "" : "s"}`} sub={usd0(data.upcoming.revenue_cents)} />
      </div>
      <div className="mt-3 rounded-xl bg-birdie-cream/60 px-3 py-2.5">
        <Row label="If you add this many events each month">
          <NumberInput
            prefix="+"
            value={s.growth_events_month}
            onChange={(v) => update({ growth_events_month: Math.min(20, v ?? 0) })}
            label="Events added each month"
            width="w-20"
          />
        </Row>
        {projection && <p className="text-[12px] text-birdie-charcoal/75">{projection}</p>}
      </div>
    </Card>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-birdie-green/15 px-3 py-2 min-w-0">
      <div className="text-[11px] text-birdie-charcoal/55">{label}</div>
      <div className="text-base font-semibold text-birdie-charcoal truncate">{value}</div>
      {sub && <div className="text-[11px] text-birdie-charcoal/50 truncate">{sub}</div>}
    </div>
  );
}

// Columns for events per month, with the target as a solid hairline.
function EventsChart({ months, target, height = 150 }) {
  const [hover, setHover] = useState(null);
  const top = Math.max(4, ...months.map((x) => x.events), target || 0);
  const max = Math.ceil(top * 1.15);
  const last = months.length - 1;
  const ty = target ? (1 - target / max) * 100 : null;
  return (
    <div className="flex gap-2" role="group" aria-label="Events by month">
      <div className="relative w-6 shrink-0 text-[10px] text-birdie-charcoal/45 tabular-nums" style={{ height }}>
        {[0, Math.round(max / 2), max].map((t) => (
          <div key={t} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - t / max) * 100}%` }}>
            {t}
          </div>
        ))}
      </div>
      <div className="flex-1 min-w-0">
        <div className="relative" style={{ height }}>
          <div className="absolute inset-x-0 bottom-0 border-t border-birdie-charcoal/15" />
          {ty != null && (
            <>
              <div className="absolute inset-x-0" style={{ top: `${ty}%`, borderTop: `1.5px solid ${INK}` }} />
              <div className="absolute right-0 -translate-y-full pb-0.5 text-[10px] font-semibold text-birdie-charcoal" style={{ top: `${ty}%` }}>
                Need {target}
              </div>
            </>
          )}
          <div className="absolute inset-0 flex items-end">
            {months.map((x, i) => {
              const h = (x.events / max) * 100;
              const label = `${monthName(x.month)}${i === last ? " (so far)" : ""}: ${x.events} event${x.events === 1 ? "" : "s"}, ${formatMoney(x.profit_cents)} profit`;
              return (
                <button
                  key={x.month}
                  type="button"
                  aria-label={label}
                  onMouseEnter={() => setHover(i)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                  onClick={() => setHover(i)}
                  className="relative flex-1 min-w-0 h-full flex items-end justify-center focus:outline-none"
                >
                  <span
                    className="block w-[60%] max-w-[22px] rounded-t"
                    style={{ height: `${h}%`, minHeight: x.events ? 2 : 0, background: MARK, opacity: i === last ? 0.55 : hover === null || hover === i ? 1 : 0.5 }}
                  />
                  {hover === i && (
                    <span
                      className={`absolute z-10 ${i < 2 ? "left-0" : i > last - 2 ? "right-0" : "left-1/2 -translate-x-1/2"} bottom-1/2 rounded-lg bg-birdie-charcoal text-white text-[11px] px-2 py-1.5 whitespace-nowrap shadow-lg pointer-events-none text-left`}
                    >
                      <span className="block font-semibold">
                        {x.events} event{x.events === 1 ? "" : "s"}
                      </span>
                      <span className="block">{formatMoney(x.profit_cents)} profit</span>
                      <span className="block opacity-70">
                        {monthName(x.month)}
                        {i === last ? " so far" : ""}
                      </span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex mt-1">
          {months.map((x, i) => (
            <div key={x.month} className="flex-1 min-w-0 text-center text-[10px] text-birdie-charcoal/45 overflow-hidden whitespace-nowrap">
              {i % 2 === last % 2 ? monthName(x.month, 0, { month: "short" }) : ""}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WhatIf({ m, s }) {
  const [events, setEvents] = useState(m.eventsNeeded || 8);
  const [price, setPrice] = useState(Math.round(m.price / 100));
  const left = events * m.profitAt(price * 100) - m.overhead; // profit after fixed costs
  const pay = Math.min(left, m.payMonth);
  const extra = left - m.payMonth; // beyond your pay: split by ownership
  const share = m.payMonth > 0 ? left / m.payMonth : 0;
  const maxEvents = Math.max(s.max_events_month * 2, (m.eventsNeeded || 0) + 6, 12);
  return (
    <Card title="What if" sub="Slide to see what a month like this would pay you. Profit beyond your pay is split by ownership.">
      <label className="block">
        <span className="flex justify-between text-sm text-birdie-charcoal">
          Events a month <span className="font-semibold tabular-nums">{events}</span>
        </span>
        <input type="range" min={0} max={maxEvents} value={events} onChange={(e) => setEvents(Number(e.target.value))} className="w-full accent-birdie-green" />
      </label>
      <label className="block mt-2">
        <span className="flex justify-between text-sm text-birdie-charcoal">
          Average booking <span className="font-semibold tabular-nums">${price.toLocaleString()}</span>
        </span>
        <input type="range" min={500} max={6000} step={50} value={price} onChange={(e) => setPrice(Number(e.target.value))} className="w-full accent-birdie-green" />
      </label>
      <div className="mt-3 rounded-xl bg-birdie-cream/60 px-3 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm text-birdie-charcoal/70">Your pay</span>
          <span className="text-xl font-semibold text-birdie-charcoal">{usd0(Math.max(0, pay))} / month</span>
        </div>
        <div className="h-2 rounded-full bg-white mt-2" role="meter" aria-valuenow={Math.round(share * 100)} aria-valuemin={0} aria-valuemax={100} aria-label="Share of your number">
          <div className="h-full rounded-full" style={{ width: `${Math.max(1.5, Math.min(1, Math.max(0, share)) * 100)}%`, background: MARK }} />
        </div>
        <div className="text-[11px] text-birdie-charcoal/55 mt-1">
          {extra >= 0
            ? `All of your ${usd0(m.payMonth)} a month, plus ${usd0(extra)} profit to split with Gabe by ownership`
            : `${Math.max(0, Math.round(share * 100))}% of your ${usd0(m.payMonth)} a month, ${usd0(-extra)} short`}
          {events > s.max_events_month ? ` · more than the ${s.max_events_month} events you said you can do` : ""}
        </div>
      </div>
    </Card>
  );
}

function SafetyNet({ m, s, update }) {
  const runwayGoal = s.personal_monthly_cents * s.runway_months;
  const reserveGoal = m.overhead * s.reserve_months;
  return (
    <Card title="Safety net" sub="Savings that let a slow month be a slow month, not a crisis.">
      <div className="text-xs font-semibold text-birdie-charcoal/60">You</div>
      <Row label="Personal spending" hint="per month, everything">
        <NumberInput prefix="$" value={dollars(s.personal_monthly_cents)} onChange={(v) => update({ personal_monthly_cents: toCents(v ?? 0) })} label="Personal spending per month" width="w-24" />
      </Row>
      <Row label="Months of runway" hint="6 is common, 12 is comfortable">
        <NumberInput value={s.runway_months} onChange={(v) => update({ runway_months: Math.min(36, Math.round(v ?? 0)) })} label="Months of runway" width="w-20" />
      </Row>
      <Row label="Saved so far">
        <NumberInput prefix="$" value={dollars(s.runway_saved_cents)} onChange={(v) => update({ runway_saved_cents: toCents(v ?? 0) })} label="Runway saved" width="w-24" />
      </Row>
      <Meter have={s.runway_saved_cents} goal={runwayGoal} empty="Add your monthly spending to set the goal." />

      <div className="text-xs font-semibold text-birdie-charcoal/60 mt-4">The business</div>
      <Row label="Cash in the business account" hint="today's Crews balance">
        <NumberInput prefix="$" value={dollars(s.business_cash_cents)} onChange={(v) => update({ business_cash_cents: toCents(v ?? 0) })} label="Business cash" width="w-24" />
      </Row>
      <Row label="Months of fixed costs to keep">
        <NumberInput value={s.reserve_months} onChange={(v) => update({ reserve_months: Math.min(24, Math.round(v ?? 0)) })} label="Months of fixed costs" width="w-20" />
      </Row>
      <Meter have={s.business_cash_cents} goal={reserveGoal} empty="Add your fixed costs to set the goal." />
    </Card>
  );
}

function Meter({ have, goal, empty }) {
  if (!goal) return <p className="text-[11px] text-birdie-charcoal/50 mt-1">{empty}</p>;
  const f = Math.max(0, Math.min(1, have / goal));
  return (
    <div className="mt-1">
      <div className="h-2 rounded-full bg-birdie-green/10" role="meter" aria-valuenow={Math.round(f * 100)} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full" style={{ width: `${Math.max(1.5, f * 100)}%`, background: MARK }} />
      </div>
      <div className="text-[11px] text-birdie-charcoal/55 mt-1">
        {usd0(have)} of {usd0(goal)} · {f >= 1 ? "done" : `${usd0(goal - have)} to go`}
      </div>
    </div>
  );
}

function PlanList({ s, m, update }) {
  const isDone = (it) => (it.gate ? m.gates[it.gate].ok : !!s.checks[it.id]);
  const all = PHASES.flatMap((p) => p.items);
  const doneN = all.filter(isDone).length;
  return (
    <Card title="The plan" sub={`${doneN} of ${all.length} steps done. Green steps with “tracked” update themselves from your numbers.`}>
      <div className="h-2 rounded-full bg-birdie-green/10 mb-4" role="meter" aria-valuenow={doneN} aria-valuemin={0} aria-valuemax={all.length} aria-label="Plan progress">
        <div className="h-full rounded-full" style={{ width: `${Math.max(1.5, (doneN / all.length) * 100)}%`, background: MARK }} />
      </div>
      <div className="grid lg:grid-cols-2 gap-x-6 gap-y-5">
        {PHASES.map((p) => {
          const n = p.items.filter(isDone).length;
          return (
            <div key={p.key}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-sm font-semibold text-birdie-charcoal">{p.title}</div>
                <div className="text-[11px] text-birdie-charcoal/45 shrink-0">
                  {n}/{p.items.length}
                </div>
              </div>
              {p.when && <div className="text-[11px] text-birdie-charcoal/50">{p.when}</div>}
              <ul className="mt-1.5 space-y-1">
                {p.items.map((it) => {
                  const on = isDone(it);
                  const body = (
                    <>
                      <span
                        className={`mt-0.5 h-5 w-5 shrink-0 rounded-md flex items-center justify-center text-[11px] font-bold ${
                          on ? "bg-birdie-green text-white" : "border-2 border-birdie-charcoal/20 bg-white"
                        }`}
                        aria-hidden
                      >
                        {on ? "✓" : ""}
                      </span>
                      <span className="min-w-0">
                        <span className={`block text-sm ${on ? "text-birdie-charcoal/55 line-through decoration-birdie-charcoal/25" : "text-birdie-charcoal"}`}>
                          {it.t}
                          {it.gate && <span className="ml-1.5 text-[10px] font-semibold text-birdie-charcoal/40 no-underline">tracked</span>}
                        </span>
                        {it.d && !on && <span className="block text-[11px] text-birdie-charcoal/55">{it.d}</span>}
                      </span>
                    </>
                  );
                  return (
                    <li key={it.id}>
                      {it.gate ? (
                        <div className="flex items-start gap-2.5 rounded-lg px-1.5 py-1.5 -mx-1.5">{body}</div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => update({ checks: { [it.id]: !on } }, true)}
                          aria-pressed={on}
                          className="w-full flex items-start gap-2.5 rounded-lg px-1.5 py-1.5 -mx-1.5 text-left hover:bg-birdie-cream/60"
                        >
                          {body}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
